import React, { useState, useEffect } from 'react';
import { X, Clock, AlertTriangle, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

export const TrialBanner = ({ subscription }) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(true);
  const [daysRemaining, setDaysRemaining] = useState(null);

  useEffect(() => {
    if (!subscription) return;
    if (subscription.status !== 'trialing') return;

    // Tage berechnen (priority: trial_days_remaining → trial_ends_at → trial_end)
    if (subscription.trial_days_remaining !== undefined && subscription.trial_days_remaining !== null) {
      setDaysRemaining(subscription.trial_days_remaining);
    } else if (subscription.trial_ends_at) {
      const diffDays = Math.ceil((new Date(subscription.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24));
      setDaysRemaining(diffDays);
    } else if (subscription.trial_end) {
      const diffDays = Math.ceil((new Date(subscription.trial_end) - new Date()) / (1000 * 60 * 60 * 24));
      setDaysRemaining(diffDays);
    } else {
      setDaysRemaining(0);
    }
  }, [subscription]);

  if (!visible || !subscription) return null;

  const { status, billing_status } = subscription;
  const disc = subscription?.stripe_discount_info;

  // 🔕 Kein Banner wenn billing_status/stripe_status korrekt 'active' ist
  // (verhindert Banner bei stale status='trialing' obwohl Billing schon aktiv)
  if (billing_status === 'active' || subscription?.stripe_status === 'active') return null;

  // 🔕 Kein Banner für dauerhaft 100%-rabattierte Accounts
  if (disc && disc.percent_off === 100 && disc.duration === 'forever') return null;

  // 🟢 TRIAL ACTIVE - Show days remaining (only when > 0 days left)
  if (status === 'trialing' && daysRemaining !== null && daysRemaining > 0) {
    // 🆕 NEUE LOGIK: Banner IMMER anzeigen mit verschiedenen Farben
    let bannerColor = '';
    let textColor = '';
    let iconColor = '';
    let secondaryTextColor = '';
    let buttonVariant = 'secondary';
    
    if (daysRemaining >= 8) {
      // 🟢 GREEN: 30-8 Tage (entspannt)
      bannerColor = 'bg-green-100 dark:bg-green-950/30 border-b-2 border-green-500';
      textColor = 'text-green-900 dark:text-green-100';
      iconColor = 'text-green-600 dark:text-green-400';
      secondaryTextColor = 'text-green-700 dark:text-green-300';
      buttonVariant = 'secondary';
    } else if (daysRemaining >= 4) {
      // 🔵 BLUE: 7-4 Tage (aufmerksam)
      bannerColor = 'bg-blue-100 dark:bg-blue-950/30 border-b-2 border-blue-500';
      textColor = 'text-blue-900 dark:text-blue-100';
      iconColor = 'text-blue-600 dark:text-blue-400';
      secondaryTextColor = 'text-blue-700 dark:text-blue-300';
      buttonVariant = 'secondary';
    } else if (daysRemaining >= 1) {
      // 🟠 ORANGE: 3-1 Tage (dringend)
      bannerColor = 'bg-orange-100 dark:bg-orange-950/30 border-b-2 border-orange-500';
      textColor = 'text-orange-900 dark:text-orange-100';
      iconColor = 'text-orange-600 dark:text-orange-400';
      secondaryTextColor = 'text-orange-700 dark:text-orange-300';
      buttonVariant = 'default';
    } else {
      // Sollte nicht passieren, Fallback zu orange
      bannerColor = 'bg-orange-100 dark:bg-orange-950/30 border-b-2 border-orange-500';
      textColor = 'text-orange-900 dark:text-orange-100';
      iconColor = 'text-orange-600 dark:text-orange-400';
      secondaryTextColor = 'text-orange-700 dark:text-orange-300';
      buttonVariant = 'default';
    }

    return (
      <div className={`w-full px-3 py-2 sm:px-4 sm:py-3 ${bannerColor}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <Clock className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${iconColor}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-xs sm:text-sm font-medium truncate ${textColor}`}>
                <span className="hidden sm:inline">Kostenlose Testphase: Noch </span>
                <span className="sm:hidden">Trial: </span>
                <strong className="text-sm sm:text-base">{daysRemaining} {daysRemaining === 1 ? 'Tag' : 'Tage'}</strong>
                <span className="hidden sm:inline"> kostenlos</span>
              </p>
              {/* Preistext: nur anzeigen wenn KEIN 100% Dauerrabatt aktiv */}
              {(() => {
                const disc = subscription?.stripe_discount_info;
                const is100Forever = disc && disc.percent_off === 100 && disc.duration === 'forever';
                const is100Once = disc && disc.percent_off === 100;
                if (is100Forever) {
                  return (
                    <p className={`text-xs hidden sm:block font-medium ${secondaryTextColor}`}>
                      Danach: <strong>0,00 € / Monat</strong> dank dauerhaftem 100% Rabatt
                    </p>
                  );
                }
                if (is100Once) {
                  return (
                    <p className={`text-xs hidden sm:block ${secondaryTextColor}`}>
                      Danach: ab 29,90 € Netto/Monat (Gutschein: 1 Monat 100% Rabatt aktiv)
                    </p>
                  );
                }
                return (
                  <p className={`text-xs hidden sm:block ${secondaryTextColor}`}>
                    Danach: ab 29,90 € Netto/Monat (zzgl. 19% MwSt.)
                  </p>
                );
              })()}
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant={buttonVariant}
              onClick={() => navigate('/admin/subscription')}
              className="text-xs sm:text-sm px-2 py-1 sm:px-3 sm:py-2 h-auto whitespace-nowrap"
            >
              <span className="hidden sm:inline">Zahlungsmethode</span>
              <span className="sm:hidden">Details</span>
            </Button>
            <button
              onClick={() => setVisible(false)}
              className="p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 🔴 TRIAL ENDED – Banner komplett ausblenden (Backend liefert min. 0, nie negativ)
  // Wenn daysRemaining = 0 → Trial heute abgelaufen → kein Banner mehr anzeigen
  if (status === 'trialing' && daysRemaining !== null && daysRemaining <= 0) {
    return null; // Komplett ausblenden
  }

  // ⚠️ PAYMENT FAILED (past_due)
  if (billing_status === 'past_due' || status === 'past_due') {
    return (
      <div className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-amber-100 dark:bg-amber-950/30 border-b-2 border-amber-500">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-semibold text-amber-900 dark:text-amber-100 truncate">
                Zahlungsproblem
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300 hidden sm:block">
                Bitte aktualisieren Sie Ihre Zahlungsmethode
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="default"
              onClick={() => navigate('/admin/subscription')}
              className="text-xs sm:text-sm px-2 py-1 sm:px-3 sm:py-2 h-auto bg-amber-600 hover:bg-amber-700 whitespace-nowrap"
            >
              <span className="hidden sm:inline">Zahlung aktualisieren</span>
              <span className="sm:hidden">Zahlung</span>
            </Button>
            <button
              onClick={() => setVisible(false)}
              className="p-1 hover:bg-black/10 dark:hover:bg-white/10 rounded"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
